import axios from "axios";
import { maskJWTToken, maskUserData } from "../utils/security";

// Disable console logging in production to prevent data leakage
const isDevelopment = process.env.NODE_ENV === "development";
const shouldLogSensitiveData =
  isDevelopment && process.env.REACT_APP_LOG_SENSITIVE_DATA === "true";

const secureLog = (message, data) => {
  if (shouldLogSensitiveData) {
  } else {
  }
};

// API configuration - Get from environment variables
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://gard4grad-backend.fly.dev";

if (!API_BASE_URL && process.env.NODE_ENV !== "production") {
  console.warn("REACT_APP_API_URL is not set. Using proxy for API requests.");
}

if (!API_BASE_URL && process.env.NODE_ENV === "production") {
  throw new Error(
    "REACT_APP_API_URL environment variable is required in production. Please check your .env files."
  );
}

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // Enable cookies
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    // Check if we're on Safari or in production and have tokens in localStorage
    const isSafari =
      /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isProduction = process.env.NODE_ENV === "production";

    if (isSafari || isProduction) {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add token refresh functionality
const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/refresh`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ refreshToken }),
      }
    );

    if (!response.ok) {
      throw new Error("Refresh failed");
    }

    const data = await response.json();

    // Store new tokens
    localStorage.setItem("accessToken", data.accessToken);
    if (data.refreshToken) {
      localStorage.setItem("refreshToken", data.refreshToken);
    }

    return data.accessToken;
  } catch (error) {
    // Clear tokens and redirect to login
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    window.location.href = "/login";
    throw error;
  }
};

// Add response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 or 403 and we haven't tried to refresh yet
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();

        // Update the original request with new token
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        return Promise.reject(refreshError);
      }
    } else if (error.response?.status === 429) {
      // Handle rate limiting - no logging in production
      if (process.env.NODE_ENV !== "production") {
        console.warn("Rate limit exceeded:", error.response.data);
      }
    } else if (error.response?.status === 403) {
      // Handle forbidden access - no logging in production
      if (process.env.NODE_ENV !== "production") {
        console.warn("Access denied:", error.response.data);
      }
    } else if (error.code === "ECONNABORTED") {
      // Handle timeout errors - no logging in production
      if (process.env.NODE_ENV !== "production") {
        console.warn("Request timeout:", error.message);
      }
    }
    return Promise.reject(error);
  }
);

// API endpoints
const API_ENDPOINTS = {
  UNIVERSITIES: {
    LIST: "/api/universities",
    DETAIL: "/api/universities",
    SEARCH: "/api/universities/search", // Full data for authenticated users
  },
  PUBLIC_UNIVERSITIES: {
    LIST: "/api/public/universities",
    DETAIL: "/api/public/universities",
    SEARCH: "/api/public/universities/search", // Limited data for public users
  },
  AUTH: {
    GOOGLE: "/api/auth/google",
    GOOGLE_CALLBACK: "/api/auth/google/callback",
    LOGOUT: "/api/auth/logout",
    VERIFY: "/api/auth/verify",
    VERIFY_GOOGLE_TOKEN: "/api/auth/verify-google-token",
  },
  BLOGS: {
    LIST: "/api/blogs",
    DETAIL: (slug) => `/api/blogs/${slug}`,
  },
  CONTACT: {
    SUBMIT: "/api/contact/submit",
  },
  ABOUT: "/api/about",
  RATE_MY_CHANCES: {
    GENERAL: "/api/rate-my-chances/general",
    SPECIFIC: "/api/rate-my-chances/specific",
    SUGGESTIONS: "/api/rate-my-chances/suggestions",
  },
};

export default api;
export { API_ENDPOINTS };
